/**
 * Ticket Evaluator
 * Developer tool to test if a ticket would trigger an on-call notification
 */

import React, { useState } from 'react';
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  MessageBarType,
  Separator,
  Icon,
  Link,
  Spinner,
  SpinnerSize
} from '@fluentui/react';
import { evaluateTicket } from '../api';
import type { TicketEvaluationResult } from '../types';
import { useConnectWiseLinks } from '@/connectwise';
import { formatUtcAsLocalDateTime } from '../utils/dateTime';

interface TicketEvaluatorProps {
  onNotification: (type: MessageBarType, message: string) => void;
}

const TicketEvaluator: React.FC<TicketEvaluatorProps> = ({ onNotification }) => {
  const { ticketUrl } = useConnectWiseLinks();
  const [ticketId, setTicketId] = useState('');
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [result, setResult] = useState<TicketEvaluationResult | null>(null);

  const handleEvaluate = async () => {
    const id = parseInt(ticketId, 10);
    if (isNaN(id) || id <= 0) {
      onNotification(MessageBarType.error, 'Please enter a valid ticket ID');
      return;
    }

    setIsEvaluating(true);
    setResult(null);
    
    try {
      const evaluation = await evaluateTicket(id);
      setResult(evaluation);
      
      if (evaluation.wouldTriggerNotification) {
        const kind = evaluation.matchedAlertKind ?? 'unknown';
        onNotification(MessageBarType.success, `This ticket WOULD trigger an on-call notification (${kind})`);
      } else {
        onNotification(MessageBarType.warning, 'This ticket would NOT trigger an on-call notification');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to evaluate ticket';
      onNotification(MessageBarType.error, `Error: ${message}`);
    } finally {
      setIsEvaluating(false);
    }
  };

  const renderCriteriaCheck = (label: string, value: boolean) => (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
      <Icon
        iconName={value ? 'CheckMark' : 'Cancel'}
        styles={{
          root: {
            color: value ? '#107c10' : '#d13438',
            fontSize: 16,
            fontWeight: 600
          }
        }}
      />
      <Text variant="medium" styles={{ root: { fontWeight: 500 } }}>
        {label}
      </Text>
    </Stack>
  );

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Stack>
        <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
          Ticket Evaluator
        </Text>
        <Text variant="medium" styles={{ root: { color: '#605e5c', marginTop: 8 } }}>
          Developer tool to test if a ticket would trigger an on-call notification based on current criteria.
        </Text>
      </Stack>

      <Stack
        tokens={{ childrenGap: 16, padding: 20 }}
        styles={{
          root: {
            backgroundColor: 'white',
            color: '#323130',
            borderRadius: 4,
            boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,0.132), 0 0.3px 0.9px 0 rgba(0,0,0,0.108)'
          }
        }}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end">
            <Stack.Item grow>
              <TextField
                label="Ticket ID"
                value={ticketId}
                onChange={(_, value) => setTicketId(value || '')}
                type="number"
                placeholder="Enter ConnectWise ticket ID"
                disabled={isEvaluating}
              />
            </Stack.Item>
            <PrimaryButton
              text={isEvaluating ? 'Evaluating...' : 'Evaluate'}
              onClick={handleEvaluate}
              disabled={isEvaluating || !ticketId}
              styles={{ root: { marginBottom: 2 } }}
            />
          </Stack>

          {isEvaluating && (
            <Stack horizontalAlign="center" tokens={{ padding: 20 }}>
              <Spinner size={SpinnerSize.large} label="Evaluating ticket..." />
            </Stack>
          )}

          {result && !isEvaluating && (
            <Stack tokens={{ childrenGap: 16 }}>
              <Separator />

              <Stack tokens={{ childrenGap: 8 }}>
                <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                  Ticket Information
                </Text>
                
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                  <Text variant="medium" styles={{ root: { fontWeight: 500 } }}>
                    Ticket:
                  </Text>
                  <Link
                    href={ticketUrl(result.ticketId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    #{result.ticketId}
                  </Link>
                </Stack>

                <Text variant="medium">
                  <strong>Summary:</strong> {result.summary}
                </Text>
                <Text variant="medium">
                  <strong>Board:</strong> {result.boardName}
                </Text>
                <Text variant="medium">
                  <strong>Status:</strong> {result.statusName}
                </Text>
                <Text variant="medium">
                  <strong>Sub-Type:</strong> {result.subTypeName || 'None'}
                </Text>
                <Text variant="medium">
                  <strong>Item:</strong> {result.itemName || 'None'}
                </Text>
                <Text variant="medium">
                  <strong>Priority:</strong> {result.priorityName || 'None'}
                </Text>
                <Text variant="medium">
                  <strong>Owner:</strong> {result.owner}
                </Text>
                {result.dateEntered && (
                  <Text variant="medium">
                    <strong>Date Entered:</strong> {formatUtcAsLocalDateTime(result.dateEntered)}
                  </Text>
                )}
              </Stack>

              <Separator />

              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                  Voicemail Rule
                </Text>

                {renderCriteriaCheck('Board matches target', result.criteria.isTargetBoard)}
                <Text variant="small" styles={{ root: { color: '#605e5c', marginLeft: 28 } }}>
                  Target boards: {result.criteria.targetBoards.join(', ')}
                </Text>

                {renderCriteriaCheck('Summary matches pattern', result.criteria.isTargetSummary)}
                <Text variant="small" styles={{ root: { color: '#605e5c', marginLeft: 28 } }}>
                  Target patterns: {result.criteria.targetSummaryPatterns.join(', ')}
                </Text>

                {renderCriteriaCheck('Status matches target', result.criteria.isTargetStatus)}
                <Text variant="small" styles={{ root: { color: '#605e5c', marginLeft: 28 } }}>
                  Target statuses: {result.criteria.targetStatuses.join(', ')}
                </Text>

                {renderCriteriaCheck('Voicemail rule matches', result.criteria.matchesVoicemail)}
              </Stack>

              <Separator />

              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                  MCIS Rule
                </Text>

                {renderCriteriaCheck('Board matches MCIS', result.criteria.isMcisBoard)}
                <Text variant="small" styles={{ root: { color: '#605e5c', marginLeft: 28 } }}>
                  MCIS boards: {result.criteria.mcisBoards.join(', ')}
                </Text>

                {renderCriteriaCheck('Status matches MCIS', result.criteria.isMcisStatus)}
                <Text variant="small" styles={{ root: { color: '#605e5c', marginLeft: 28 } }}>
                  MCIS statuses: {result.criteria.mcisStatuses.join(', ')}
                </Text>

                {renderCriteriaCheck('Sub-Type is System Generated', result.criteria.isMcisSubType)}
                <Text variant="small" styles={{ root: { color: '#605e5c', marginLeft: 28 } }}>
                  Expected: {result.criteria.mcisSubType}
                </Text>

                {renderCriteriaCheck('Item is in MCIS list', result.criteria.isMcisItem)}
                <Text variant="small" styles={{ root: { color: '#605e5c', marginLeft: 28 } }}>
                  {result.criteria.mcisItems.length} configured MCIS items
                </Text>

                {renderCriteriaCheck('Priority is P1 or P2', result.criteria.isMcisPriority)}
                <Text variant="small" styles={{ root: { color: '#605e5c', marginLeft: 28 } }}>
                  Allowed priorities: {result.criteria.mcisPriorities.join(', ')}
                </Text>

                {renderCriteriaCheck('MCIS rule matches', result.criteria.matchesMcis)}
              </Stack>

              <Separator />

              <Stack
                tokens={{ childrenGap: 12, padding: 16 }}
                styles={{
                  root: {
                    backgroundColor: result.wouldTriggerNotification ? '#dff6dd' : '#fef0f1',
                    borderRadius: 4,
                    border: `2px solid ${result.wouldTriggerNotification ? '#107c10' : '#d13438'}`
                  }
                }}
              >
                <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                  <Icon
                    iconName={result.wouldTriggerNotification ? 'CompletedSolid' : 'StatusErrorFull'}
                    styles={{
                      root: {
                        color: result.wouldTriggerNotification ? '#107c10' : '#d13438',
                        fontSize: 24
                      }
                    }}
                  />
                  <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                    {result.wouldTriggerNotification ? 'WOULD TRIGGER' : 'WOULD NOT TRIGGER'}
                  </Text>
                </Stack>
                <Text variant="medium">
                  {result.wouldTriggerNotification
                    ? `This ticket matches the ${result.matchedAlertKind} rule and would trigger an on-call notification.`
                    : 'This ticket does not meet any on-call criteria and would not trigger a notification.'}
                </Text>
              </Stack>

              {result.hasExistingCheck && result.existingCheck && (
                <>
                  <Separator />
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                      Existing Check
                    </Text>
                    <Text variant="medium">
                      This ticket already has a scheduled notification check.
                    </Text>
                    {result.existingCheck.alertKind && (
                      <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                        Alert kind: {result.existingCheck.alertKind}
                      </Text>
                    )}
                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                      Scheduled: {formatUtcAsLocalDateTime(result.existingCheck.scheduledCheckTime)}
                    </Text>
                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                      Check performed: {result.existingCheck.checkPerformed ? 'Yes' : 'No'}
                    </Text>
                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                      Notification sent: {result.existingCheck.notificationSent ? 'Yes' : 'No'}
                    </Text>
                  </Stack>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default TicketEvaluator;
